import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  type AlertDataResponse,
  type AlertingVessels,
  type ParsedAlertDataResponse,
  ROIType,
} from '../maritime-menu-options/alert-panel/alertingVessels.model';
import type {
  FleetsFilters,
  FleetsListVessel,
  FleetsVessel,
  FleetsVesselFilter,
} from '../maritime-menu-options/fleets-panel/fleets.model';
import type { HistoricVesselPoint } from '../maritime-menu-options/history-panel/historic-vessel-point.model';
import type { VesselSourceFilter } from '../maritime-menu-options/my-fleet-panel/myFleet-panel.slice';
import type { MyFleetVessel } from '../maritime-menu-options/my-fleet-panel/myFleetVessels.model';
import {
  AlertTypesByTenant,
  DEFAULT_ALERTS,
  type EAlertTypes,
} from '../models/alerts/alert-configuration';
import { mapVesselSubTypeToSuperType } from '../models/vessels/all-ship-types';
import { type Vessel, VesselSource } from '../models/vessels/vessel.model';
import { setAlertingVessels } from '../state/alerts/alerts.slice';
import { setError } from '../state/vessels/vessels.slice';
import store from '../store';
import type TenantId from '../tenant';
import type { Token } from '../user/user.slice';
import { wrapRequest } from './base';
import { getMostRecentVesselLocationsByMmsi } from './internal-ais-api-v3-helpers';

dayjs.extend(utc);

export interface GeoniusVesselData {
  vessel_id: string;
  vessel_name: string;
  imo: string;
  current_mmsi: string;
  vessel_type: string;
  company_id: string;
}
interface RiskIntelligenceVesselData {
  shiptype: any;
  callsign: any;
  name: string;
  time: string;
  course: number;
  speed: number;
  imo: number;
  position: {
    latitude: number;
    longitude: number;
  };
}

export const getVesselsForCompany = async (
  companyId: string
): Promise<GeoniusVesselData[]> =>
  wrapRequest('get', 'geonius', `/vessels`, {
    queryStringParameters: {
      company_id: companyId,
      limit: 1000,
    },
  });

export const getRiskIntelligenceVessels = async (
  company: string
): Promise<RiskIntelligenceVesselData[]> =>
  wrapRequest('get', 'geonius', '/risk-intelligence/vessels', {
    queryStringParameters: {
      company,
    },
  });

const getAlertingVesselsForCompany = async (): Promise<AlertDataResponse[]> => {
  const PAGE_SIZE = 1000;
  const allResults: AlertDataResponse[] = [];
  let offset = 0;

  while (true) {
    // biome-ignore lint/performance/noAwaitInLoops: We're paginating through the results, so we need to await each page before moving on to the next one.
    const page: AlertDataResponse[] = await wrapRequest(
      'get',
      'geonius',
      `/alerting-vessels`,
      {
        queryStringParameters: {
          limit: PAGE_SIZE,
          offset,
        },
      }
    );

    allResults.push(...page);

    if (page.length < PAGE_SIZE) {
      break;
    }

    offset += PAGE_SIZE;
  }

  return allResults;
};

export const getFleetWithLocationsForCompany = async (
  companyId: string,
  _useImos: boolean = true
): Promise<MyFleetVessel[]> => {
  const companyVessels = await getVesselsForCompany(companyId);
  const mmsis = companyVessels.map((vessel) =>
    parseInt(vessel.current_mmsi, 10)
  );

  const locationByMmsi = await getMostRecentVesselLocationsByMmsi(
    mmsis,
    'get-company-vessels-with-locations'
  );
  const fleet = companyVessels
    .map((vessel) => {
      const msg = locationByMmsi.get(vessel.current_mmsi);
      const vesselSubtype = msg?.vesselType ?? vessel.vessel_type;
      const vesselSuperType = mapVesselSubTypeToSuperType(vesselSubtype);

      return {
        mmsi: vessel.current_mmsi,
        imo: vessel.imo,
        vessel_id: vessel.vessel_id,
        name: vessel.vessel_name,
        vessel_sub_type: vesselSubtype,
        vessel_super_type: vesselSuperType,
        flag: msg?.flag,
        heading: msg?.heading,
        latitude: msg?.latitude ?? 0,
        longitude: msg?.longitude ?? 0,
        course: msg?.cog ?? 0,
        timestamp:
          msg?.posDt !== undefined
            ? new Date(msg.posDt * 1000).toISOString()
            : '',
        speed: msg?.sog,
        callsign: msg?.callsign ?? '',
        source: VesselSource.AIS, // Everything from AIS DB is an AIS vessel
      } as MyFleetVessel;
    })
    .filter((vessel) => vessel !== null);

  return fleet;
};

export const getVesselsWithLocationsForFleet = async (
  vessels: FleetsListVessel[],
  vesselListId: string
): Promise<FleetsVessel[]> => {
  const mmsis = vessels.map((vessel) => parseInt(vessel.current_mmsi, 10));
  const locationByMmsi = await getMostRecentVesselLocationsByMmsi(
    mmsis,
    'get-fleet-with-locations'
  );
  return vessels.map((vessel) => {
    const msg = locationByMmsi.get(vessel.current_mmsi);
    const vesselSubtype = msg?.vesselType ?? vessel.vessel_type;
    const vesselSuperType = mapVesselSubTypeToSuperType(vesselSubtype);
    return {
      mmsi: vessel.current_mmsi,
      imo: vessel.imo,
      vessel_id: vessel.vessel_id,
      vessel_list_id: [vesselListId],
      name: vessel.vessel_name,
      vessel_super_type: vesselSuperType,
      vessel_sub_type: vesselSubtype,
      flag: msg?.flag,
      heading: msg?.heading,
      latitude: msg?.latitude,
      longitude: msg?.longitude,
      course: msg?.cog,
      timestamp:
        msg?.posDt !== undefined
          ? new Date(msg.posDt * 1000).toISOString()
          : undefined,
      speed: msg?.sog,
      callsign: msg?.callsign,
      source: VesselSource.AIS,
    };
  });
};

export const applyVesselsFilter = (
  vessels: MyFleetVessel[],
  filters: FleetsFilters
): MyFleetVessel[] =>
  vessels?.filter((vessel) => {
    const matchByText = () => {
      const nameMatch =
        vessel.name &&
        vessel.name
          .toLocaleLowerCase()
          .includes(filters.search.toLocaleLowerCase());

      const flagMatch =
        vessel.flag &&
        vessel.flag
          .toLocaleLowerCase()
          .includes(filters.search.toLocaleLowerCase());

      const imoMatch =
        vessel.imo &&
        vessel.imo.toString().includes(filters.search.toLocaleString());

      const mmsiMatch =
        vessel.mmsi &&
        vessel.mmsi.toString().includes(filters.search.toLocaleString());

      return nameMatch || flagMatch || imoMatch || mmsiMatch;
    };

    const matchBySourceFilter = (
      v: MyFleetVessel,
      filter: VesselSourceFilter
    ) =>
      (filter.ais && v.source === VesselSource.AIS) ||
      (filter.riskIntelligence && v.source === VesselSource.RISK_INTELLIGENCE);

    const matchByVesselListFilter = (
      v: MyFleetVessel,
      vesselListsVessels: FleetsVesselFilter[]
    ) =>
      vesselListsVessels.some((vesselListVessel) => {
        if (
          vesselListVessel.name === v.name &&
          vesselListVessel.imo === `${v.imo}` &&
          vesselListVessel.mmsi === `${v.mmsi}`
        ) {
          return true;
        }
        return false;
      });

    const anySourceFilterSelected = () =>
      filters.vesselSource.ais || filters.vesselSource.riskIntelligence;

    const vesselListFilterSelected = () =>
      filters.vesselListsVessels.length &&
      filters.vesselListsVessels.length > 0;

    const matchesSourceFilter =
      !anySourceFilterSelected() ||
      matchBySourceFilter(vessel, filters.vesselSource);

    const matchesVesselListFilter =
      !vesselListFilterSelected() ||
      matchByVesselListFilter(vessel, filters.vesselListsVessels);

    return matchByText() && matchesSourceFilter && matchesVesselListFilter;
  });

const getRiskIntelligenceFleetForCompany = async (
  companyId: string
): Promise<MyFleetVessel[]> => {
  const riVessels = await getRiskIntelligenceVessels(companyId);
  const riFleet = riVessels.map((vessel) => {
    const vesselSubtype = vessel.shiptype;
    return {
      name: vessel.name,
      imo: vessel.imo,
      course: vessel.course,
      speed: vessel.speed,
      callsign: vessel.callsign,
      vessel_sub_type: vesselSubtype,
      vessel_super_type: mapVesselSubTypeToSuperType(vesselSubtype),
      latitude: vessel.position.latitude,
      longitude: vessel.position.longitude,
      timestamp: vessel.time,
      vessel_id: `${vessel.imo}`,
      source: VesselSource.RISK_INTELLIGENCE,
    };
  });

  return riFleet;
};

export const historicPointToMyFleetVessel = (
  message: HistoricVesselPoint
): Vessel => ({
  vessel_id: message.vessel_id,
  name: message.name,
  longitude: message.longitude ?? 0,
  latitude: message.latitude ?? 0,
  course: message.course ?? 0,
  speed: message.speed ?? 0,
  heading: message.heading ?? 0,
  source: VesselSource.AIS,
  callsign: message.callsign ?? '',
  vessel_sub_type: message.vessel_sub_type ?? '',
  vessel_super_type: mapVesselSubTypeToSuperType(message.vessel_sub_type),
  imo: message.imo ?? '',
  mmsi: message.mmsi ?? '',
  timestamp: message.timestamp,
});

export const getFormattedSearchDates = (
  startDate: Date | string,
  endDate: Date | string
): { searchStartDate: string; searchEndDate: string } => {
  const searchStartDate = dayjs(startDate).utc().toISOString();
  const searchEndDate = dayjs(endDate).utc().toISOString();
  return {
    searchStartDate: searchStartDate.split('.')[0],
    searchEndDate: searchEndDate.split('.')[0],
  };
};

export const getAlertingVesselsWithLocations = async (
  companyId: string
): Promise<AlertingVessels[]> => {
  const [alertDataResponse, companyVessels] = await Promise.all([
    getAlertingVesselsForCompany(),
    getVesselsForCompany(companyId),
  ]);

  const vesselIdByMmsi = new Map(
    companyVessels.map((v) => [v.current_mmsi, v.vessel_id])
  );

  const alertData = alertDataResponse.map((response) => {
    const resp: ParsedAlertDataResponse = {
      alertType: response.alert_type,
      alertTime: response.alert_time,
      expiryTime: response.expiry_time,
      name: response.vessel_name,
      type: response.vessel_type,
      additionalDetails: response.additional_details,
      alertConfigId: response.alert_config_id,
      mmsi: response.current_mmsi,
      imo: response.imo,
    };
    if (resp.alertType === 'ENTER_ROI' || resp.alertType === 'EXIT_ROI') {
      if (response.alert_modifiers.area_id) {
        resp.roiType = ROIType.MARITIME_AREA;
        resp.roiId = String(response.alert_modifiers.area_id);
        return resp;
      }
      if (response.route_id) {
        resp.roiType = ROIType.ROUTE;
        resp.roiId = response.route_id;
        return resp;
      }
      if (response.boundary_id) {
        resp.roiType = ROIType.BOUNDARY;
        resp.roiId = response.boundary_id;
        return resp;
      }
      if (response.region_id) {
        resp.roiType = ROIType.DRAWING;
        resp.roiId = response.region_id;
        return resp;
      }
    }
    return resp;
  });

  const alertingVessels: AlertingVessels[] = [];
  const vesselMmsis: number[] = alertDataResponse.map(({ current_mmsi }) =>
    parseInt(current_mmsi, 10)
  );

  // last known locations should not be queried if no mmsis found
  if (!vesselMmsis.length) {
    return alertingVessels;
  }

  const locationByMmsi = await getMostRecentVesselLocationsByMmsi(
    vesselMmsis,
    'load-alerting-vessels'
  );
  alertData.forEach((alert) => {
    const vessel_id = vesselIdByMmsi.get(alert.mmsi);
    if (!vessel_id) {
      return;
    }
    const msg = locationByMmsi.get(alert.mmsi);
    if (!msg || msg.latitude === undefined || msg.longitude === undefined) {
      // Vessel has no valid location message
      return;
    }
    alertingVessels.push({
      vessel_id,
      name: msg.vesselName ?? alert.name,
      type: alert.type,
      course: msg.cog ?? 0,
      heading: msg.heading ?? 0,
      latitude: msg.latitude,
      longitude: msg.longitude,
      callsign: msg.callsign ?? '',
      vessel_sub_type: msg.vesselType ?? '',
      vessel_super_type: mapVesselSubTypeToSuperType(msg.vesselType),
      timestamp: msg.posDt * 1000,
      speed: msg.sog,
      imo: msg.imo !== undefined ? String(msg.imo) : alert.imo,
      mmsi: String(msg.mmsi),
      alertConfigId: alert.alertConfigId,
      alertType: alert.alertType,
      alertTime: alert.alertTime,
      expiryTime: alert.expiryTime,
      additionalDetails: alert.additionalDetails,
      roiType: alert.roiType,
      roiId: alert.roiId,
      source: VesselSource.AIS,
    });
  });
  return alertingVessels;
};

export const getMyFleetData = async (
  companyId: string | undefined,
  tenantId: string | undefined,
  aisAccess: boolean,
  riAccess: boolean
): Promise<MyFleetVessel[]> => {
  if (!companyId) {
    throw new Error('No company ID provided');
  }
  const fleetPromises = [];

  if (aisAccess) {
    fleetPromises.push(getFleetWithLocationsForCompany(companyId));
  }

  if (riAccess) {
    fleetPromises.push(getRiskIntelligenceFleetForCompany(companyId));
  }
  const responses = await Promise.all(fleetPromises);
  const fleet = responses.flat();

  return fleet;
};

export async function loadAlertingVesselsWithLocations(idToken: Token | null) {
  return getAlertingVesselsWithLocations(idToken?.companyId ?? '')
    .then((response) => {
      // Set the Alerting Vessels based on the user's Tenant
      const visibleAlertTypes =
        AlertTypesByTenant[idToken?.tenantId as TenantId] || DEFAULT_ALERTS;
      const filteredAlertingVessels = response.filter((vessel) =>
        visibleAlertTypes.includes(vessel.alertType as EAlertTypes)
      );
      store.dispatch(setAlertingVessels(filteredAlertingVessels));
    })
    .catch(() => {
      store.dispatch(setError());
    });
}
